/**
 * Gestion des couches MapLibre : traces, marqueurs, détours, radars.
 *
 * Portage de `MapManager.kt`. Deux conventions de l'application Android sont
 * reprises telles quelles, parce que l'apparence en dépend :
 *
 *  - la visibilité d'une trace passe par l'**opacité**, pas par la propriété
 *    `visibility` — ce qui permet de conserver l'ordre des couches ;
 *  - l'ordre est reconstruit en **deux passes** (toutes les lignes, puis tous
 *    les marqueurs), sans quoi un marqueur peut se retrouver sous la ligne
 *    d'une autre trace.
 */

import type { Map as MapLibreMap } from 'maplibre-gl'
import type {
  Feature,
  FeatureCollection as GeoJsonFeatureCollection,
  GeoJsonProperties,
  Geometry,
} from 'geojson'

import type { RadarPoint } from '../core/radars'
import type { GpxTrack } from '../core/track'
import { ICON_CATEGORIES, IconRegistry, iconSlot } from './iconRegistry'
import {
  getRoadOverlaySchema,
  getStyleDescriptor,
  SATELLITE_ATTRIBUTION,
  SATELLITE_LAYER_ID,
  SATELLITE_MAX_ZOOM,
  SATELLITE_ROADS_FADE_END,
  SATELLITE_ROADS_FADE_START,
  SATELLITE_ROADS_LAYER_ID,
  SATELLITE_ROADS_OPACITY,
  SATELLITE_SOURCE_ID,
  SATELLITE_TILE_SIZE,
  SATELLITE_TILE_URL,
  type MapStyleId,
} from '../core/mapStyles'
import {
  createDiamondIcon,
  createFictiveStartIcon,
  createFinishIcon,
  createKmIcon,
  createRadarIcon,
  createStartFinishIcon,
  createStartIcon,
  CUSTOM_MARKER_SIZE,
  iconKey,
  type MarkerImage,
} from './markerIcons'
import { haversine, METERS_PER_DEGREE_LAT } from '../core/geo'
import { MARKER_TYPE_WAYOUT } from '../core/types'

/** Distance sous laquelle départ et arrivée sont fusionnés en une icône. */
const CLOSE_THRESHOLD_M = 50.0

/** Largeur des lignes de trace, constante quel que soit le zoom. */
const TRACK_LINE_WIDTH = 4

/** Rangs d'empilement des marqueurs, du plus bas au plus haut. */
const SORT_KEY = {
  KM: 1,
  CUSTOM: 2,
  REAL_START: 3,
  START_END: 4,
} as const

export const RADAR_SOURCE_ID = 'radar-source'
export const RADAR_LAYER_ID = 'radar-layer'

const ACCURACY_SOURCE_ID = 'accuracy-source'
const ACCURACY_FILL_ID = 'accuracy-fill'
const ACCURACY_LINE_ID = 'accuracy-line'

/** Sommets du polygone approchant le cercle de précision. */
const ACCURACY_SEGMENTS = 72

/** Jeu vide partagé, pour vider un créneau du registre d'icônes. */
const EMPTY_KEYS: ReadonlySet<string> = new Set()


type MapFeature = Feature<Geometry, GeoJsonProperties>
type FeatureCollection = GeoJsonFeatureCollection<Geometry, GeoJsonProperties>

/**
 * Identifiants des couches d'une trace.
 *
 * Regroupés pour que l'ajout, la suppression et le réordonnancement ne
 * puissent pas diverger sur un identifiant oublié.
 */
function layerIds(trackId: string) {
  return {
    line: `line-${trackId}`,
    way: `way-layer-${trackId}`,
    wayVia: `way-via-layer-${trackId}`,
    waypoints: `wpt-layer-${trackId}`,
    km: `km-layer-${trackId}`,
    custom: `custom-layer-${trackId}`,
    realStart: `realstart-layer-${trackId}`,
    startEnd: `startend-layer-${trackId}`,
  }
}

function sourceIds(trackId: string) {
  return {
    line: `source-${trackId}`,
    way: `way-source-${trackId}`,
    wayVia: `way-via-source-${trackId}`,
    waypoints: `wpt-source-${trackId}`,
    km: `km-source-${trackId}`,
    custom: `custom-source-${trackId}`,
    realStart: `realstart-source-${trackId}`,
    startEnd: `startend-source-${trackId}`,
  }
}

export class MapLayerManager {
  #registeredImages = new Set<string>()

  /**
   * Clés d'icônes vivantes, par trace et par catégorie.
   *
   * `#registeredImages` dit ce qui a été posé, jamais ce qui sert encore : il
   * ne faisait que croître, et chaque changement de couleur, d'intervalle ou
   * de mode sombre abandonnait un jeu complet dans l'atlas de textures.
   */
  #iconRegistry = new IconRegistry()

  #trackOrder: string[] = []

  /**
   * Version de géométrie déjà envoyée à la source de ligne, par trace.
   *
   * `updateTrack` est appelée à chaque changement de réglage, de marqueur ou
   * d'ordre du tiroir ; reconstruire à chaque fois les entités d'une trace de
   * deux cent mille points coûte des centaines de milliers d'allocations pour
   * une géométrie inchangée. L'application Android tient le même cache
   * (`MapManager.lineFeaturesCache`).
   */
  #lineVersions = new Map<string, number>()

  /**
   * Abscisses curvilignes des points d'intérêt, par trace.
   *
   * Chacune coûte une projection sur toute la trace : sans mémoïsation, un
   * fichier de cinq mille points d'intérêt sur une trace dense fait des
   * dizaines de millions d'appels à `haversine` **à chaque redessin**.
   * Équivalent de `MapManager.waypointDistanceCache`, validé comme lui par la
   * géométrie **et** par l'identité de la liste de points d'intérêt.
   *
   * Seules les distances sont retenues, pas les entités : leur libellé dépend
   * du départ réel, qui doit pouvoir changer sans invalider la projection.
   */
  #waypointDistances = new Map<
    string,
    {
      version: number
      waypoints: readonly unknown[]
      distances: readonly (number | null)[]
    }
  >()

  constructor(
    private readonly map: MapLibreMap,
    private readonly options: {
      markerScale: () => number
      kmMarkerInterval: () => number
      showKmMarkers: () => boolean
      customMarkerDarkMode: () => boolean
    },
  ) {}

  /** Identifiants des couches interrogeables au clic, marqueurs d'abord. */
  clickableLayerIds(): string[] {
    const ids: string[] = []
    for (const trackId of this.#trackOrder) {
      const l = layerIds(trackId)
      ids.push(l.custom, l.startEnd, l.realStart, l.waypoints, l.wayVia)
    }
    ids.push(RADAR_LAYER_ID)
    return ids.filter((id) => this.map.getLayer(id) !== undefined)
  }

  /* -------------------------------------------------------------------
   * Images
   * ---------------------------------------------------------------- */

  #ensureImage(key: string, build: () => MarkerImage): void {
    if (this.#registeredImages.has(key) && this.map.hasImage(key)) {
      return
    }
    const image = build()
    if (this.map.hasImage(key)) {
      this.map.removeImage(key)
    }
    this.map.addImage(key, image.data, { pixelRatio: image.pixelRatio })
    this.#registeredImages.add(key)
  }

  /**
   * Retire du style les icônes d'un créneau devenues orphelines.
   *
   * Le registre décide, cette méthode applique — c'est la seule part qui a
   * besoin d'une carte, et elle tient en trois lignes.
   */
  #replaceIconKeys(slot: string, keys: ReadonlySet<string>): void {
    for (const key of this.#iconRegistry.replace(slot, keys)) {
      if (this.map.hasImage(key)) this.map.removeImage(key)
      this.#registeredImages.delete(key)
    }
  }

  /** À rappeler après un changement de style : les images sont perdues avec lui. */
  resetImageCache(): void {
    this.#registeredImages.clear()
    this.#iconRegistry.clear()
  }

  /* -------------------------------------------------------------------
   * Traces
   * ---------------------------------------------------------------- */

  addTrack(track: GpxTrack): void {
    if (!this.#trackOrder.includes(track.id)) {
      this.#trackOrder.push(track.id)
    }
    this.updateTrack(track)
  }

  /** Reconstruit toutes les couches d'une trace à partir de son état courant. */
  updateTrack(track: GpxTrack): void {
    const opacity = track.visible ? 1 : 0
    const src = sourceIds(track.id)
    const lay = layerIds(track.id)

    // --- Ligne principale ---
    // La géométrie n'est réenvoyée que si elle a changé — ou si la source a
    // disparu avec le style précédent.
    if (
      this.map.getSource(src.line) === undefined ||
      this.#lineVersions.get(track.id) !== track.geometryVersion
    ) {
      this.#setSource(src.line, this.#buildLineFeatures(track))
      this.#lineVersions.set(track.id, track.geometryVersion)
    }
    this.#ensureLayer(lay.line, {
      id: lay.line,
      type: 'line',
      source: src.line,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': track.color,
        'line-width': TRACK_LINE_WIDTH,
        'line-opacity': opacity,
      },
    })
    this.map.setPaintProperty(lay.line, 'line-color', track.color)
    this.map.setPaintProperty(lay.line, 'line-opacity', opacity)

    // --- Détours (way in/out), en pointillé ---
    this.#setSource(src.way, this.#buildWayFeatures(track))
    this.#ensureLayer(lay.way, {
      id: lay.way,
      type: 'line',
      source: src.way,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': track.color,
        'line-width': TRACK_LINE_WIDTH,
        'line-opacity': opacity,
        'line-dasharray': [2, 2],
      },
    })
    this.map.setPaintProperty(lay.way, 'line-color', track.color)
    this.map.setPaintProperty(lay.way, 'line-opacity', opacity)

    // --- Points de passage des détours ---
    this.#setSource(src.wayVia, this.#buildWayViaFeatures(track))
    this.#ensureLayer(lay.wayVia, {
      id: lay.wayVia,
      type: 'circle',
      source: src.wayVia,
      paint: {
        'circle-radius': 5,
        'circle-color': track.color,
        'circle-stroke-color': '#FFFFFF',
        'circle-stroke-width': 1.5,
        'circle-opacity': opacity,
        'circle-stroke-opacity': opacity,
      },
    })
    this.map.setPaintProperty(lay.wayVia, 'circle-color', track.color)
    this.map.setPaintProperty(lay.wayVia, 'circle-opacity', opacity)
    this.map.setPaintProperty(lay.wayVia, 'circle-stroke-opacity', opacity)

    // --- Points d'intérêt du fichier ---
    this.#setSource(src.waypoints, this.#buildWaypointFeatures(track))
    this.#ensureLayer(lay.waypoints, {
      id: lay.waypoints,
      type: 'circle',
      source: src.waypoints,
      paint: {
        'circle-radius': 6,
        'circle-color': track.color,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
        'circle-opacity': opacity,
        'circle-stroke-opacity': opacity,
      },
    })
    this.map.setPaintProperty(lay.waypoints, 'circle-color', track.color)
    this.map.setPaintProperty(lay.waypoints, 'circle-opacity', opacity)
    this.map.setPaintProperty(lay.waypoints, 'circle-stroke-opacity', opacity)

    this.#updateSymbolLayers(track, opacity)
    this.reorderLayers()
  }

  #updateSymbolLayers(track: GpxTrack, opacity: number): void {
    const src = sourceIds(track.id)
    const lay = layerIds(track.id)
    const priority = this.#priorityOf(track.id)
    const scale = this.options.markerScale()

    // --- Pastilles kilométriques ---
    const kmFeatures: MapFeature[] = []
    const kmKeys = new Set<string>()
    if (this.options.showKmMarkers()) {
      for (const point of track.generateKmMarkerPoints(
        this.options.kmMarkerInterval(),
      )) {
        const key = iconKey('km', track.color, point.label)
        this.#ensureImage(key, () => createKmIcon(track.color, point.label))
        kmKeys.add(key)
        kmFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
          properties: { icon: key, track_id: track.id },
        })
      }
    }
    this.#setSource(src.km, { type: 'FeatureCollection', features: kmFeatures })
    // Après la source, jamais avant : purger d'abord retirerait des images que
    // les entités encore en place référencent.
    this.#replaceIconKeys(iconSlot(track.id, 'km'), kmKeys)
    this.#ensureSymbolLayer(lay.km, src.km, priority * 10 + SORT_KEY.KM, scale)
    this.map.setPaintProperty(lay.km, 'icon-opacity', opacity)

    // --- Marqueurs personnalisés ---
    const darkMode = this.options.customMarkerDarkMode()
    const customFeatures: MapFeature[] = []
    const customKeys = new Set<string>()

    for (const [index, marker] of track.customMarkers.entries()) {
      if (!marker.visible) continue

      const point = track.pointAtDistance(
        track.markerKmToRawDistance(marker.distanceKm),
      )
      if (!point) continue

      const key = iconKey(
        'custom',
        track.color,
        `${darkMode ? 'd' : 'l'}-${marker.symbol}`,
      )
      this.#ensureImage(key, () =>
        createDiamondIcon(track.color, marker.symbol, darkMode, CUSTOM_MARKER_SIZE),
      )
      customKeys.add(key)

      customFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
        properties: {
          icon: key,
          title: marker.name,
          description:
            marker.description.length > 0
              ? `${marker.distanceKm.toFixed(2)} km\n${marker.description}`
              : `${marker.distanceKm.toFixed(2)} km`,
          track_id: track.id,
          marker_index: index,
          is_way_out: marker.type === MARKER_TYPE_WAYOUT,
        },
      })
    }

    this.#setSource(src.custom, {
      type: 'FeatureCollection',
      features: customFeatures,
    })
    this.#replaceIconKeys(iconSlot(track.id, 'custom'), customKeys)
    this.#ensureSymbolLayer(
      lay.custom,
      src.custom,
      priority * 10 + SORT_KEY.CUSTOM,
      scale,
    )
    this.map.setPaintProperty(lay.custom, 'icon-opacity', opacity)

    // --- Départ, arrivée, départ réel ---
    const points = track.allPoints
    const startEndFeatures: MapFeature[] = []
    /**
     * Android enregistre les cinq icônes de base d'un coup, qu'elles servent ou
     * non ; le portage ne fabrique que celles dont la trace a besoin. On
     * n'enregistre donc que celles-là — poser un départ réel rend le départ
     * simple orphelin, et il est purgé si aucune autre trace de la même couleur
     * ne s'en sert.
     */
    const baseKeys = new Set<string>()

    if (points.length > 0) {
      const first = points[0]!
      const last = points[points.length - 1]!
      const isLoop =
        points.length > 1 &&
        haversine(first.lat, first.lon, last.lat, last.lon) < CLOSE_THRESHOLD_M

      const hasRealStart = track.hasRealStart

      if (isLoop) {
        const key = iconKey('start-end', track.color)
        this.#ensureImage(key, () => createStartFinishIcon(track.color))
        baseKeys.add(key)
        startEndFeatures.push(
          this.#pointFeature(first, {
            icon: key,
            title: track.name,
            marker: 'start-end',
            track_id: track.id,
          }),
        )
      } else {
        const startKey = hasRealStart
          ? iconKey('fictive-start', track.color)
          : iconKey('start', track.color)
        this.#ensureImage(startKey, () =>
          hasRealStart
            ? createFictiveStartIcon(track.color)
            : createStartIcon(track.color),
        )
        baseKeys.add(startKey)
        startEndFeatures.push(
          this.#pointFeature(first, {
            icon: startKey,
            title: track.name,
            marker: hasRealStart ? 'fictive-start' : 'start',
            track_id: track.id,
          }),
        )

        const endKey = iconKey('end', track.color)
        this.#ensureImage(endKey, () => createFinishIcon(track.color))
        baseKeys.add(endKey)
        startEndFeatures.push(
          this.#pointFeature(last, {
            icon: endKey,
            title: track.name,
            marker: 'end',
            track_id: track.id,
          }),
        )
      }
    }

    this.#setSource(src.startEnd, {
      type: 'FeatureCollection',
      features: startEndFeatures,
    })
    this.#ensureSymbolLayer(
      lay.startEnd,
      src.startEnd,
      priority * 10 + SORT_KEY.START_END,
      scale,
    )
    this.map.setPaintProperty(lay.startEnd, 'icon-opacity', opacity)

    const realStartFeatures: MapFeature[] = []
    if (track.hasRealStart) {
      const point = track.pointAtDistance(track.realStartDistance)
      if (point) {
        const key = iconKey('realstart', track.color)
        this.#ensureImage(key, () => createStartIcon(track.color))
        baseKeys.add(key)
        realStartFeatures.push(
          this.#pointFeature(point, {
            icon: key,
            title: track.name,
            marker: 'real-start',
            track_id: track.id,
          }),
        )
      }
    }

    this.#setSource(src.realStart, {
      type: 'FeatureCollection',
      features: realStartFeatures,
    })
    this.#ensureSymbolLayer(
      lay.realStart,
      src.realStart,
      priority * 10 + SORT_KEY.REAL_START,
      scale,
    )
    this.map.setPaintProperty(lay.realStart, 'icon-opacity', opacity)

    // Après les deux sources : les icônes de base se partagent entre le bloc
    // départ/arrivée et celui du départ réel, le jeu n'est complet qu'ici.
    this.#replaceIconKeys(iconSlot(track.id, 'base'), baseKeys)
  }

  removeTrack(trackId: string): void {
    // Avant le retrait des couches, comme `MapManager.removeTrack` : tout est
    // synchrone, aucune image n'est dessinée entre les deux. Ce qui protège
    // les icônes d'une autre trace de même couleur, c'est le registre.
    for (const category of ICON_CATEGORIES) {
      this.#replaceIconKeys(iconSlot(trackId, category), EMPTY_KEYS)
    }
    for (const id of Object.values(layerIds(trackId))) {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    }
    for (const id of Object.values(sourceIds(trackId))) {
      if (this.map.getSource(id)) this.map.removeSource(id)
    }
    this.#trackOrder = this.#trackOrder.filter((id) => id !== trackId)
    this.#lineVersions.delete(trackId)
    this.#waypointDistances.delete(trackId)
  }

  /** Fixe l'ordre d'affichage : le premier de la liste passe devant. */
  setTrackOrder(order: readonly string[]): void {
    this.#trackOrder = [...order]
    this.reorderLayers()
  }

  /**
   * Remonte les couches dans le bon ordre.
   *
   * Les lignes d'abord, tous marqueurs ensuite : `symbol-sort-key` n'ordonne
   * qu'entre symboles d'une même couche, il ne peut pas empêcher une ligne de
   * recouvrir le marqueur d'une autre trace.
   */
  reorderLayers(): void {
    const reversed = [...this.#trackOrder].reverse()

    for (const trackId of reversed) {
      const lay = layerIds(trackId)
      this.#moveToTop(lay.line)
      this.#moveToTop(lay.way)
    }

    for (const trackId of reversed) {
      const lay = layerIds(trackId)
      this.#moveToTop(lay.wayVia)
      this.#moveToTop(lay.waypoints)
      this.#moveToTop(lay.km)
      this.#moveToTop(lay.custom)
      this.#moveToTop(lay.realStart)
      this.#moveToTop(lay.startEnd)
    }

    this.#moveToTop(RADAR_LAYER_ID)
  }

  #priorityOf(trackId: string): number {
    const index = this.#trackOrder.indexOf(trackId)
    return index === -1 ? 0 : this.#trackOrder.length - 1 - index
  }

  #moveToTop(layerId: string): void {
    if (this.map.getLayer(layerId)) {
      this.map.moveLayer(layerId)
    }
  }

  /* -------------------------------------------------------------------
   * Radars
   * ---------------------------------------------------------------- */

  setRadars(radars: readonly RadarPoint[], visible: boolean): void {
    const features: MapFeature[] = radars.map((radar) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [radar.lon, radar.lat] },
      properties: {
        radar_id: radar.id,
        speed_limit: radar.speedLimit,
      },
    }))

    this.#setSource(RADAR_SOURCE_ID, { type: 'FeatureCollection', features })

    this.#ensureImage('icon-radar', () => createRadarIcon())

    if (!this.map.getLayer(RADAR_LAYER_ID)) {
      this.map.addLayer({
        id: RADAR_LAYER_ID,
        type: 'symbol',
        source: RADAR_SOURCE_ID,
        layout: {
          'icon-image': 'icon-radar',
          'icon-size': this.options.markerScale(),
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      })
    }

    // Les radars utilisent `visibility` et non l'opacité : contrairement aux
    // traces, ils n'ont pas d'ordre relatif à préserver.
    this.map.setLayoutProperty(
      RADAR_LAYER_ID,
      'visibility',
      visible ? 'visible' : 'none',
    )
    this.#moveToTop(RADAR_LAYER_ID)
  }

  /**
   * Rafraîchit les seuls points de passage d'une trace.
   *
   * Sert au glissement d'un point : reconstruire toutes les couches à chaque
   * image reconstruirait aussi la ligne principale, jusqu'à deux cent mille
   * points, pour déplacer un rond de cinq pixels.
   */
  updateWayVias(track: GpxTrack): void {
    this.#setSource(sourceIds(track.id).wayVia, this.#buildWayViaFeatures(track))
  }

  /**
   * Cercle de précision autour de la position.
   *
   * Dessiné comme un polygone en coordonnées géographiques plutôt qu'avec un
   * rayon en pixels : il grandit et rétrécit alors avec le zoom sans aucun
   * calcul, et reste juste sous une carte inclinée.
   *
   * @param position `null` pour l'effacer.
   */
  setAccuracyCircle(
    position: { lat: number; lon: number; accuracy: number } | null,
  ): void {
    const features: MapFeature[] =
      position === null || position.accuracy <= 0
        ? []
        : [
            {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [
                  circleRing(position.lat, position.lon, position.accuracy),
                ],
              },
              properties: {},
            },
          ]

    this.#setSource(ACCURACY_SOURCE_ID, { type: 'FeatureCollection', features })

    this.#ensureLayer(ACCURACY_FILL_ID, {
      id: ACCURACY_FILL_ID,
      type: 'fill',
      source: ACCURACY_SOURCE_ID,
      paint: { 'fill-color': '#4285F4', 'fill-opacity': 0.15 },
    })

    this.#ensureLayer(ACCURACY_LINE_ID, {
      id: ACCURACY_LINE_ID,
      type: 'line',
      source: ACCURACY_SOURCE_ID,
      paint: {
        'line-color': '#4285F4',
        'line-width': 1,
        'line-opacity': 0.4,
      },
    })
  }

  /* -------------------------------------------------------------------
   * Vue satellite
   * ---------------------------------------------------------------- */

  /**
   * Active ou désactive la vue satellite.
   *
   * Le raster est inséré **sous** la première couche applicative : posé
   * au-dessus, il masquerait les traces.
   */
  setSatellite(enabled: boolean, styleId: MapStyleId): void {
    if (!enabled) {
      if (this.map.getLayer(SATELLITE_LAYER_ID)) {
        this.map.setLayoutProperty(SATELLITE_LAYER_ID, 'visibility', 'none')
      }
      if (this.map.getLayer(SATELLITE_ROADS_LAYER_ID)) {
        this.map.setLayoutProperty(SATELLITE_ROADS_LAYER_ID, 'visibility', 'none')
      }
      return
    }

    if (!this.map.getSource(SATELLITE_SOURCE_ID)) {
      this.map.addSource(SATELLITE_SOURCE_ID, {
        type: 'raster',
        tiles: [SATELLITE_TILE_URL],
        tileSize: SATELLITE_TILE_SIZE,
        maxzoom: SATELLITE_MAX_ZOOM,
        attribution: SATELLITE_ATTRIBUTION,
      })
    }

    if (!this.map.getLayer(SATELLITE_LAYER_ID)) {
      this.map.addLayer(
        {
          id: SATELLITE_LAYER_ID,
          type: 'raster',
          source: SATELLITE_SOURCE_ID,
        },
        this.#firstApplicationLayerId(),
      )
    }
    this.map.setLayoutProperty(SATELLITE_LAYER_ID, 'visibility', 'visible')

    this.#ensureRoadOverlay(styleId)
  }

  /**
   * Trame routière blanche par-dessus le satellite.
   *
   * Elle s'estompe entre les zooms 15 et 17 : de près, l'imagerie se suffit à
   * elle-même et la trame gênerait la lecture du terrain.
   */
  #ensureRoadOverlay(styleId: MapStyleId): void {
    const style = getStyleDescriptor(styleId)
    const schema = getRoadOverlaySchema(style)

    const vectorSourceId = this.#firstVectorSourceId()
    if (!vectorSourceId) return

    if (this.map.getLayer(SATELLITE_ROADS_LAYER_ID)) {
      this.map.removeLayer(SATELLITE_ROADS_LAYER_ID)
    }

    this.map.addLayer(
      {
        id: SATELLITE_ROADS_LAYER_ID,
        type: 'line',
        source: vectorSourceId,
        'source-layer': schema.sourceLayer,
        // `match` plutôt qu'une disjonction de comparaisons : une seule
        // évaluation par entité, et le type est correctement inféré.
        filter: [
          'match',
          ['get', schema.classProperty],
          [...schema.classes],
          true,
          false,
        ],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#FFFFFF',
          'line-width': [
            'interpolate',
            ['exponential', 1.4],
            ['zoom'],
            8,
            0.7,
            13,
            1.8,
            16,
            4,
            19,
            8,
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            SATELLITE_ROADS_FADE_START,
            SATELLITE_ROADS_OPACITY,
            SATELLITE_ROADS_FADE_END,
            0,
          ],
        },
      },
      this.#firstApplicationLayerId(),
    )
  }

  #firstVectorSourceId(): string | null {
    const style = this.map.getStyle()
    for (const [id, source] of Object.entries(style.sources ?? {})) {
      if ((source as { type?: string }).type === 'vector') return id
    }
    return null
  }

  /** Première couche appartenant à l'application, servant d'ancre d'insertion. */
  #firstApplicationLayerId(): string | undefined {
    const style = this.map.getStyle()
    const prefixes = [
      'line-',
      'way-layer-',
      'way-via-layer-',
      'wpt-layer-',
      'km-layer-',
      'custom-layer-',
      'startend-layer-',
      'realstart-layer-',
      RADAR_LAYER_ID,
    ]

    for (const layer of style.layers ?? []) {
      if (prefixes.some((prefix) => layer.id.startsWith(prefix))) {
        return layer.id
      }
    }
    return undefined
  }

  /** Bâtiments en relief — seul le style Liberty embarque la couche. */
  setBuildings3d(enabled: boolean): void {
    if (this.map.getLayer('building-3d')) {
      this.map.setLayoutProperty(
        'building-3d',
        'visibility',
        enabled ? 'visible' : 'none',
      )
    }
  }

  /* -------------------------------------------------------------------
   * Fabriques de données
   * ---------------------------------------------------------------- */

  #buildLineFeatures(track: GpxTrack): FeatureCollection {
    const features: MapFeature[] = track.segments
      .filter((segment) => segment.points.length >= 2)
      .map((segment) => ({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: segment.points.map((p) => [p.lon, p.lat]),
        },
        properties: { track_id: track.id },
      }))

    return { type: 'FeatureCollection', features }
  }

  #buildWayFeatures(track: GpxTrack): FeatureCollection {
    const features: MapFeature[] = []

    for (const way of track.wayInOuts) {
      if (!way.visible) continue
      const route = track.wayRoutes.get(way.id)
      if (!route || route.length < 2) continue

      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: route.map((p) => [p.lon, p.lat]),
        },
        properties: { track_id: track.id, way_id: way.id },
      })
    }

    return { type: 'FeatureCollection', features }
  }

  #buildWayViaFeatures(track: GpxTrack): FeatureCollection {
    const features: MapFeature[] = []

    for (const way of track.wayInOuts) {
      if (!way.visible) continue
      // Les points de passage n'apparaissent que si le détour est tracé :
      // seuls, ils seraient incompréhensibles.
      if (!track.wayRoutes.has(way.id)) continue

      for (const [index, via] of way.vias.entries()) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [via.lon, via.lat] },
          properties: { track_id: track.id, way_id: way.id, via_index: index },
        })
      }
    }

    return { type: 'FeatureCollection', features }
  }

  /**
   * Abscisses curvilignes des points d'intérêt, mémoïsées.
   *
   * Un point à plus de 200 m du tracé n'en reçoit aucune : l'application
   * Android retient le point le plus proche sans plafond, ce qui donne un
   * kilomètre arbitraire à un point posé loin du parcours. **Écart assumé**,
   * voir le tableau du `README.md`.
   */
  #waypointDistancesFor(track: GpxTrack): readonly (number | null)[] {
    const cached = this.#waypointDistances.get(track.id)
    if (
      cached &&
      cached.version === track.geometryVersion &&
      cached.waypoints === track.waypoints
    ) {
      return cached.distances
    }

    const distances = track.waypoints.map(
      (waypoint) => track.projectPoint(waypoint.lat, waypoint.lon, 200)?.distance ?? null,
    )

    this.#waypointDistances.set(track.id, {
      version: track.geometryVersion,
      waypoints: track.waypoints,
      distances,
    })

    return distances
  }

  #buildWaypointFeatures(track: GpxTrack): FeatureCollection {
    // Masquer les points d'intérêt vide la trace de ses points, et la source
    // avec — la couche, elle, reste : elle garde son ordre d'empilement et son
    // style, et réafficher revient à recalculer les entités.
    const distances = this.#waypointDistancesFor(track)

    const features: MapFeature[] = track.waypoints.map((waypoint, index) => {
      const lines: string[] = []
      if (waypoint.desc) lines.push(waypoint.desc)

      const distance = distances[index] ?? null
      if (distance !== null) {
        // Borné à zéro comme sur Android : un point d'intérêt situé avant le
        // départ réel afficherait sinon un kilomètre négatif.
        const km = Math.max(track.rawDistanceToMarkerKm(distance), 0)
        lines.push(`${km.toFixed(1)} km`)
      }
      // Une altitude nulle vient d'un fichier sans données, pas du niveau
      // de la mer : l'afficher induirait en erreur. Idem sur une trace dont
      // le profil n'est pas exploitable — même condition que sur Android.
      if (waypoint.ele !== null && waypoint.ele !== 0 && track.hasElevationData) {
        lines.push(`${Math.round(waypoint.ele)} m`)
      }

      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [waypoint.lon, waypoint.lat] },
        properties: {
          title: waypoint.name ?? '',
          description: lines.join('\n'),
          track_id: track.id,
        },
      }
    })

    return { type: 'FeatureCollection', features }
  }

  #pointFeature(
    point: { lat: number; lon: number },
    properties: Record<string, unknown>,
  ): MapFeature {
    return {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
      properties,
    }
  }

  /* -------------------------------------------------------------------
   * Utilitaires MapLibre
   * ---------------------------------------------------------------- */

  #setSource(id: string, data: FeatureCollection): void {
    const source = this.map.getSource(id)
    if (source && 'setData' in source) {
      ;(source as maplibregl.GeoJSONSource).setData(data)
      return
    }
    this.map.addSource(id, { type: 'geojson', data })
  }

  #ensureLayer(id: string, spec: maplibregl.LayerSpecification): void {
    if (!this.map.getLayer(id)) {
      this.map.addLayer(spec)
    }
  }

  #ensureSymbolLayer(
    id: string,
    sourceId: string,
    sortKey: number,
    scale: number,
  ): void {
    if (!this.map.getLayer(id)) {
      this.map.addLayer({
        id,
        type: 'symbol',
        source: sourceId,
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': scale,
          // Les marqueurs ne doivent jamais être écartés par le moteur de
          // placement : un point kilométrique manquant serait trompeur.
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'symbol-sort-key': sortKey,
        },
      })
      return
    }

    this.map.setLayoutProperty(id, 'icon-size', scale)
    this.map.setLayoutProperty(id, 'symbol-sort-key', sortKey)
  }

  /** Applique une nouvelle échelle d'icônes à toutes les couches de symboles. */
  setMarkerScale(scale: number): void {
    for (const trackId of this.#trackOrder) {
      const lay = layerIds(trackId)
      for (const id of [lay.km, lay.custom, lay.realStart, lay.startEnd]) {
        if (this.map.getLayer(id)) {
          this.map.setLayoutProperty(id, 'icon-size', scale)
        }
      }
    }
    if (this.map.getLayer(RADAR_LAYER_ID)) {
      this.map.setLayoutProperty(RADAR_LAYER_ID, 'icon-size', scale)
    }
  }

  /** Ajuste la caméra sur une trace, avec la même marge que l'application. */
  fitTrack(track: GpxTrack, padding = 120): void {
    const points = track.allPoints
    if (points.length === 0) return

    if (points.length === 1) {
      this.map.jumpTo({ center: [points[0]!.lon, points[0]!.lat], zoom: 15 })
      return
    }

    let minLat = Infinity
    let maxLat = -Infinity
    let minLon = Infinity
    let maxLon = -Infinity

    for (const point of points) {
      if (point.lat < minLat) minLat = point.lat
      if (point.lat > maxLat) maxLat = point.lat
      if (point.lon < minLon) minLon = point.lon
      if (point.lon > maxLon) maxLon = point.lon
    }

    this.map.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      { padding, duration: 600 },
    )
  }
}

/**
 * Anneau de points approchant un cercle au sol.
 *
 * Le rayon est converti en degrés séparément pour la latitude et la longitude,
 * cette dernière se resserrant vers les pôles : un cercle calculé sur un seul
 * facteur serait un ovale dès qu'on quitte l'équateur.
 */
function circleRing(
  lat: number,
  lon: number,
  radiusMeters: number,
): [number, number][] {
  const latDelta = radiusMeters / METERS_PER_DEGREE_LAT
  const lonDelta =
    latDelta / Math.max(Math.cos((lat * Math.PI) / 180), 0.01)

  const ring: [number, number][] = []
  for (let i = 0; i <= ACCURACY_SEGMENTS; i += 1) {
    const angle = (i / ACCURACY_SEGMENTS) * 2 * Math.PI
    ring.push([lon + lonDelta * Math.cos(angle), lat + latDelta * Math.sin(angle)])
  }

  return ring
}
